import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';

/** One parameter offered by a `{ id, name, selected }` options endpoint. */
interface ParameterOption {
  id: string;
  name: string;
  selected?: boolean;
}

/**
 * A detail-form field that opens the checkbox popup when clicked (legacy G5 Parameter popup in the
 * detail view). The value text is shown on the button; the picked ids are handed back to the caller.
 */
export function MultiPickField({
  label,
  required,
  text,
  disabled,
  optionsUrl,
  picked,
  onApply,
}: {
  label: string;
  required?: boolean;
  /** The names shown on the button, comma-joined. */
  text: string;
  disabled: boolean;
  optionsUrl: string;
  /** Ids already picked in this form; undefined = start from the server's linked set. */
  picked?: string[];
  onApply: (ids: string[], names: Record<string, string>) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sd-row">
      <label className="sd-label">
        {label}
        {required ? <span className="req">*</span> : null}
      </label>
      <button
        type="button"
        className="sd-input"
        style={{ textAlign: 'left', width: '100%', cursor: disabled ? 'default' : 'pointer' }}
        disabled={disabled}
        title={disabled ? 'Save the record first' : 'Click to choose'}
        onClick={() => setOpen(true)}
      >
        {text || '— None —'}
      </button>
      {open && (
        <ParameterPickerDialog
          title={label}
          optionsUrl={optionsUrl}
          initialPicked={picked}
          onClose={() => setOpen(false)}
          onApply={(ids, names) => {
            onApply(ids, names);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * The checkbox popup behind a multi-select list cell (legacy G5 Parameter popup).
 *
 * Loads the options from `optionsUrl`, ticks the ones already linked (or `initialPicked` when the user
 * has already changed the selection in this row), and hands back the chosen ids plus their names.
 * Nothing is saved here — the list's row save sends the selection.
 */
export function ParameterPickerDialog({
  title,
  optionsUrl,
  initialPicked,
  onApply,
  onClose,
}: {
  title: string;
  optionsUrl: string;
  /** Ids already chosen in this row's edit; undefined = start from the server's linked set. */
  initialPicked?: string[];
  onApply: (ids: string[], names: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [options, setOptions] = useState<ParameterOption[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set(initialPicked ?? []));

  useEffect(() => {
    let alive = true;
    void api
      .get<{ data?: ParameterOption[] }>(optionsUrl, { silentLoading: true } as never)
      .then((r) => {
        if (!alive) return;
        const rows = (r.data?.data ?? []).map((o) => ({ id: String(o.id), name: String(o.name), selected: !!o.selected }));
        setOptions(rows);
        if (initialPicked === undefined) setPicked(new Set(rows.filter((o) => o.selected).map((o) => o.id)));
      });
    return () => {
      alive = false;
    };
    // The options are fetched once per open; the caller remounts this dialog to reopen it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsUrl]);

  const toggle = (id: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const apply = () => {
    const names: Record<string, string> = {};
    for (const o of options ?? []) names[o.id] = o.name;
    onApply([...picked], names);
  };

  return (
    <AppDialog
      open
      title={title}
      width={420}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ca-btn" onClick={apply} disabled={options === null}>
            OK
          </button>
        </>
      }
    >
      {options === null ? (
        <div>Loading…</div>
      ) : options.length === 0 ? (
        <div>No parameters are defined for this test.</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {options.map((o) => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px' }}>
              <input
                type="checkbox"
                checked={picked.has(o.id)}
                onChange={(e) => toggle(o.id, e.target.checked)}
              />
              <span>{o.name}</span>
            </label>
          ))}
        </div>
      )}
    </AppDialog>
  );
}
